import { useQuery, useQueryClient } from "@tanstack/react-query";
// PageHeader replaces the icon + back-link composite.
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { ErrorPanel, Loading } from "../components/Loading";
import { EditableField } from "../components/ui/EditableField";
import { PageHeader } from "../components/ui/PageHeader";
import { api } from "../lib/api";

export function LeaveTypeDetailPage() {
  const { t } = useTranslation();
  const { leaveTypeId } = useParams<{ leaveTypeId: string }>();
  const qc = useQueryClient();

  const ltQ = useQuery({
    queryKey: ["leave-type", leaveTypeId],
    queryFn: () => api.getLeaveType(leaveTypeId!),
    enabled: !!leaveTypeId,
  });

  if (ltQ.isLoading) return <Loading />;
  if (ltQ.error) return <ErrorPanel message={(ltQ.error as Error).message} />;
  if (!ltQ.data) return <ErrorPanel message={t("policies.leave_types_not_found")} />;

  const lt = ltQ.data;

  const patch = (patch: Record<string, unknown>) =>
    api.updateLeaveType(lt.id, patch).then((updated) => {
      qc.setQueryData(["leave-type", lt.id], updated);
      qc.invalidateQueries({ queryKey: ["leave-types"] });
      return updated;
    });

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={`${lt.code} · ${lt.name_en}`}
        breadcrumbs={[
          { label: t("policies.leave_types_back"), href: "/policies/leave-types" },
          { label: lt.code },
        ]}
      />

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.leave_types_section_identity")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.name_en_label")}
            value={lt.name_en}
            onSave={(v) => patch({ name_en: v })}
          />
          <EditableField
            label={t("policies.name_ar_label")}
            value={lt.name_ar}
            rtl
            onSave={(v) => patch({ name_ar: v })}
          />
          <EditableField
            label={t("policies.description_label")}
            value={lt.description}
            kind="textarea"
            onSave={(v) => patch({ description: v })}
          />
          <EditableField
            label={t("policies.leave_types_calendar_color")}
            value={lt.leave_color}
            placeholder="#10b981"
            onSave={(v) => patch({ leave_color: v })}
          />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.leave_types_cat_elig")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.leave_types_category")}
            value={lt.category}
            onSave={(v) => patch({ category: v })}
          />
          <EditableField
            label={t("policies.leave_types_ticket_category")}
            value={lt.ticket_category}
            onSave={(v) => patch({ ticket_category: v })}
          />
          <EditableField
            label={t("policies.leave_types_eligible_after_months")}
            value={lt.eligible_after_months}
            kind="number"
            onSave={(v) => patch({ eligible_after_months: Number(v) })}
          />
          <EditableField
            label={t("policies.leave_types_gender_filter")}
            value={lt.gender_filter}
            onSave={(v) => patch({ gender_filter: v })}
          />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.leave_types_balance_accrual")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.leave_types_balance_calc")}
            value={lt.balance_calculation_type}
            onSave={(v) => patch({ balance_calculation_type: v })}
          />
          <EditableField
            label={t("policies.leave_types_days_per_year")}
            value={lt.number_of_days}
            kind="number"
            onSave={(v) =>
              patch({ number_of_days: v == null ? null : String(v) })
            }
          />
          <EditableField
            label={t("policies.leave_types_allow_negative")}
            value={lt.allow_negative_balance}
            kind="boolean"
            onSave={(v) => patch({ allow_negative_balance: v })}
          />
          <EditableField
            label={t("policies.leave_types_max_negative_days")}
            value={lt.maximum_negative_days}
            kind="number"
            onSave={(v) =>
              patch({ maximum_negative_days: v == null ? null : String(v) })
            }
          />
          <EditableField
            label={t("policies.leave_types_encashable_eos")}
            value={lt.encashable_on_eos}
            kind="boolean"
            onSave={(v) => patch({ encashable_on_eos: v })}
          />
          <EditableField
            label={t("policies.leave_types_remove_holidays")}
            value={lt.remove_holidays_from_transaction}
            kind="boolean"
            onSave={(v) => patch({ remove_holidays_from_transaction: v })}
          />
          <EditableField
            label={t("policies.leave_types_remove_weekends")}
            value={lt.remove_weekends_from_transaction}
            kind="boolean"
            onSave={(v) => patch({ remove_weekends_from_transaction: v })}
          />
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.leave_types_payment_workflow")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.leave_types_pay_with_salary")}
            value={lt.pay_leave_with_salary}
            kind="boolean"
            onSave={(v) => patch({ pay_leave_with_salary: v })}
          />
          <EditableField
            label={t("policies.leave_types_requires_attachment")}
            value={lt.requires_attachment_before_request}
            kind="boolean"
            onSave={(v) => patch({ requires_attachment_before_request: v })}
          />
          <EditableField
            label={t("policies.leave_types_include_in_pay_cycle")}
            value={lt.include_in_pay_cycle}
            kind="boolean"
            onSave={(v) => patch({ include_in_pay_cycle: v })}
          />
          <EditableField
            label={t("policies.leave_types_auto_return")}
            value={lt.auto_return}
            kind="boolean"
            onSave={(v) => patch({ auto_return: v })}
          />
        </div>
      </section>

      {/* M3X — Section A (description split EN+AR) */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.leave_types_section_description", "Description")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t("policies.leave_types_description_en", "Description (EN)")}
            value={lt.description_en ?? ""}
            kind="textarea"
            onSave={(v) => patch({ description_en: v })}
          />
          <EditableField
            label={t("policies.leave_types_description_ar", "Description (AR)")}
            value={lt.description_ar ?? ""}
            kind="textarea"
            rtl
            onSave={(v) => patch({ description_ar: v })}
          />
        </div>
      </section>

      {/* M3X — Section B (eligibility filters) */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.leave_types_section_eligibility", "Eligibility filters")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t(
              "policies.leave_types_nationality_excluding",
              "Nationality excluding (comma sep ISO-3)",
            )}
            value={(lt.nationality_excluding ?? []).join(", ")}
            onSave={(v) =>
              patch({
                nationality_excluding: String(v)
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
          <EditableField
            label={t("policies.leave_types_relationship_active", "Relationship active")}
            value={lt.relationship_active ?? false}
            kind="boolean"
            onSave={(v) => patch({ relationship_active: v })}
          />
          <EditableField
            label={t(
              "policies.leave_types_office_location_restriction",
              "Office location restriction (comma sep branch UUIDs)",
            )}
            value={(lt.office_location_restriction ?? []).join(", ")}
            onSave={(v) =>
              patch({
                office_location_restriction: String(v)
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean),
              })
            }
          />
          <EditableField
            label={t(
              "policies.leave_types_maternity_infant_special_need",
              "Maternity — infant special need",
            )}
            value={lt.maternity_infant_special_need ?? false}
            kind="boolean"
            onSave={(v) => patch({ maternity_infant_special_need: v })}
          />
        </div>
      </section>

      {/* M3X — Section C (accrual & EOS) */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.leave_types_section_accrual_eos", "Accrual & EOS service")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t(
              "policies.leave_types_deduct_from_service_period_in_eos",
              "Deduct from service period in EOS (days)",
            )}
            value={lt.deduct_from_service_period_in_eos ?? null}
            kind="number"
            onSave={(v) =>
              patch({ deduct_from_service_period_in_eos: Number(v) })
            }
          />
          <EditableField
            label={t(
              "policies.leave_types_maternity_leave_special_accrual",
              "Maternity leave — special accrual",
            )}
            value={lt.maternity_leave_special_accrual ?? false}
            kind="boolean"
            onSave={(v) => patch({ maternity_leave_special_accrual: v })}
          />
          <EditableField
            label={t(
              "policies.leave_types_leave_carry_over_for_selected_year",
              "Leave carry-over for selected year",
            )}
            value={lt.leave_carry_over_for_selected_year ?? false}
            kind="boolean"
            onSave={(v) =>
              patch({ leave_carry_over_for_selected_year: v })
            }
          />
          <EditableField
            label={t(
              "policies.leave_types_allow_exceeding_balance_to_unpaid",
              "Allow exceeding balance → unpaid",
            )}
            value={lt.allow_exceeding_balance_to_unpaid ?? false}
            kind="boolean"
            onSave={(v) =>
              patch({ allow_exceeding_balance_to_unpaid: v })
            }
          />
          <EditableField
            label={t(
              "policies.leave_types_carry_over_with_pay_transaction",
              "Carry-over with pay transaction",
            )}
            value={lt.carry_over_with_pay_transaction ?? false}
            kind="boolean"
            onSave={(v) =>
              patch({ carry_over_with_pay_transaction: v })
            }
          />
        </div>
      </section>

      {/* M3X — Section D (Self-Service visibility) */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.leave_types_section_visibility", "Self-Service visibility")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t(
              "policies.leave_types_hide_sss_disable_alternative_employee",
              "SSS — disable alternative employee",
            )}
            value={lt.hide_sss_disable_alternative_employee ?? false}
            kind="boolean"
            onSave={(v) =>
              patch({ hide_sss_disable_alternative_employee: v })
            }
          />
          <EditableField
            label={t(
              "policies.leave_types_hide_sss_hide_leave_planned",
              "SSS — hide leave-planned field",
            )}
            value={lt.hide_sss_hide_leave_planned ?? false}
            kind="boolean"
            onSave={(v) => patch({ hide_sss_hide_leave_planned: v })}
          />
          <EditableField
            label={t(
              "policies.leave_types_hide_sss_hide_notes",
              "SSS — hide notes field",
            )}
            value={lt.hide_sss_hide_notes ?? false}
            kind="boolean"
            onSave={(v) => patch({ hide_sss_hide_notes: v })}
          />
          <EditableField
            label={t(
              "policies.leave_types_to_be_shown_on_mss",
              "Shown on MSS",
            )}
            value={lt.to_be_shown_on_mss ?? false}
            kind="boolean"
            onSave={(v) => patch({ to_be_shown_on_mss: v })}
          />
        </div>
      </section>

      {/* M3X — Section G (pay calculation) */}
      <section className="rounded-xl border border-slate-200 bg-white p-6">
        <h2 className="mb-4 text-sm font-semibold uppercase tracking-wider text-slate-500">
          {t("policies.leave_types_section_pay_calc", "Pay calculation")}
        </h2>
        <div className="grid gap-4 md:grid-cols-2">
          <EditableField
            label={t(
              "policies.leave_types_leave_amount_number_of_days_to_pay",
              "Leave amount — number of days to pay",
            )}
            value={lt.leave_amount_number_of_days_to_pay ?? null}
            kind="number"
            onSave={(v) =>
              patch({ leave_amount_number_of_days_to_pay: Number(v) })
            }
          />
          <EditableField
            label={t(
              "policies.leave_types_pay_rate_tier_formula_ref",
              "Pay rate — tier formula ref",
            )}
            value={lt.pay_rate_tier_formula_ref ?? ""}
            onSave={(v) => patch({ pay_rate_tier_formula_ref: v })}
          />
        </div>
      </section>
    </div>
  );
}
